import { FaMagnifyingGlass } from 'react-icons/fa6';
import { AllowlistItem } from './AllowlistItem';
import { Button } from 'components/Button';
import toast from 'react-hot-toast';
import { useGetTmsOrdersInboxes } from 'hooks/useGetTmsOrdersInboxes';
import { useInboxSenderAllowList } from 'hooks/useInboxSenderAllowlist';
import { useSearch } from 'hooks/useSearch';
import { useSort } from 'hooks/useSort';
import { useUser } from 'components/Accounts';
import { useCompanies } from 'providers/Companies';
import { isValidAdmin } from 'helpers/user';
import { DropdownButton } from 'components/Revenue360';
import { useCompanyFilter } from 'hooks/useCompanyFilter';
import itemStyles from './styles.module.css';
import styles from 'styles/Allowlist.module.css';
import classNames from 'classnames';

const InboxSenderAllowlistTable = () => {
    const { user } = useUser();
    const { search, handleSearch } = useSearch();
    const { sort, setSort } = useSort();

    const { companies } = useCompanies();
    const companyList = companies.reduce((acc, curr) => {
        acc[curr.company] = curr.company_id;
        return acc;
    }, {});

    const { company, setCompany } = useCompanyFilter();

    const [field, order] = sort ? sort.split(':') : [];

    const { data, isValidating, error, clearFilters, mutate } =
        useInboxSenderAllowList(companyList[company]);

    const showLisa = user?.allowed_features?.includes('lisa'); // TODO: make sure ‘lisa’ is added to enabled_features for the corresponding companies on the allowlist migration
    const showAutoQuote = user?.allowed_features?.includes('auto-quote');
    const showTmsOrders = false; // user?.allowed_features?.includes('order-entry');
    const showReplier = false; // user?.allowed_features?.includes('replier');
    const showWinLoss = false; // user?.allowed_features?.includes('win-loss');

    return (
        <>
            {isValidAdmin(user) && Object.keys(companyList).length > 0 && (
                // allow admin to view allowlists from different companies
                <DropdownButton
                    value={company || user?.company}
                    setValue={setCompany}
                    options={Object.keys(companyList)}
                    width='min-content'
                />
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                }}
            >
                <div className={styles.card} style={{ gap: 0 }}>
                    <div>
                        <h2 style={{ marginTop: 0 }}>Add a new recipient</h2>
                        <p style={{ fontSize: 14 }}>
                            <strong style={{ fontWeight: 600 }}>
                                "Sender Name"
                            </strong>{' '}
                            refers to the name of the <i>from recipient</i> of
                            an email (e.g. "John Smith" or "Everest Logistics").
                        </p>
                        <p style={{ marginBottom: 8, fontSize: 14 }}>
                            <strong style={{ fontWeight: 600 }}>
                                "Inbox ID"
                            </strong>{' '}
                            is the ID of an inbox, which may not be the same as
                            the inbox name.
                        </p>
                        <ul style={{ marginTop: 8, fontSize: 14 }}>
                            <li style={{ marginBottom: 8 }}>
                                For Front, the inbox ID starts with "inb_" (e.g.
                                "inb_123d" or "inb_abc4").{' '}
                                <i>
                                    Getting this ID may require assistance from
                                    Everest.
                                </i>
                            </li>
                            <li style={{ marginBottom: 8 }}>
                                For Outlook, the inbox ID will be the email
                                address used to log into the inbox (e.g.
                                "user@outlook.com").
                            </li>
                            <li style={{ marginBottom: 8 }}>
                                For Gmail, the inbox ID will be the email
                                address used to log into the inbox (e.g.
                                "user@gmail.com").
                            </li>
                            <li
                                style={{
                                    fontWeight: 500,
                                    color: 'var(--primary-500)',
                                }}
                            >
                                <strong>NOTE:</strong> This can also be left
                                blank or be a wildcard (*) to process emails
                                from <i>any inbox</i>, as long as the email's
                                from recipients match the{' '}
                                <strong style={{ fontWeight: 600 }}>
                                    "Sender Address."
                                </strong>
                            </li>
                        </ul>
                        <p style={{ marginBottom: 8, fontSize: 14 }}>
                            <strong style={{ fontWeight: 600 }}>
                                "Sender Address"
                            </strong>{' '}
                            refers to the email address or email domain of the{' '}
                            <i>from recipient</i> of an email (e.g.
                            "user@domain.com" or "domain.com").
                        </p>
                        <ul style={{ marginTop: 8, fontSize: 14 }}>
                            <li
                                style={{
                                    fontWeight: 500,
                                    color: 'var(--primary-500)',
                                }}
                            >
                                <strong>NOTE:</strong> This can also be left
                                blank or be a wildcard (*) to process{' '}
                                <i>all emails</i> in an inbox specified by the{' '}
                                <strong style={{ fontWeight: 600 }}>
                                    "Inbox ID."
                                </strong>
                            </li>
                        </ul>
                    </div>
                    <AllowlistItem selectedCompanyid={companyList[company]} />
                </div>

                <div
                    className={classNames(
                        styles.card,
                        isValidAdmin(user) &&
                            Object.keys(companyList).length > 0 &&
                            styles.shrinkHeight
                    )}
                >
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                marginBottom: 20,
                            }}
                        >
                            <h2 style={{ margin: 0 }}>Current allowlist</h2>
                            {isValidating && <div>Loading...</div>}
                        </div>
                        <p style={{ fontSize: 14 }}>
                            Feature settings can be edited by clicking the{' '}
                            <strong style={{ fontWeight: 600 }}>
                                pencil icon
                            </strong>{' '}
                            beside the dropdown inputs.
                        </p>
                    </div>
                    <div className={styles.header}>
                        <div className={styles.searchbar}>
                            <FaMagnifyingGlass className={styles.icon} />
                            <input
                                type='text'
                                defaultValue={search}
                                onChange={(e) => handleSearch(e.target.value)}
                                className={styles.search}
                                placeholder='Search by name or address...'
                            />
                        </div>
                        <div className={styles.sort}>
                            {(field || search) && (
                                <Button
                                    onClick={clearFilters}
                                    size='sm'
                                    type='button'
                                    uitype='ghost'
                                    color='red'
                                >
                                    Clear filters
                                </Button>
                            )}
                            <Button
                                onClick={() => {
                                    if (field === 'sender_name') {
                                        setSort(
                                            'sender_name',
                                            order === 'asc' ? 'desc' : 'asc'
                                        );
                                    } else {
                                        setSort('sender_name', 'asc');
                                    }
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                                color={
                                    field === 'sender_name'
                                        ? 'primary'
                                        : 'secondary-dark'
                                }
                            >
                                {field === 'sender_name'
                                    ? order === 'asc'
                                        ? 'Sort by name (asc)'
                                        : 'Sort by name (desc)'
                                    : 'Sort by name'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (field === 'inbox') {
                                        setSort(
                                            'inbox',
                                            order === 'asc' ? 'desc' : 'asc'
                                        );
                                    } else {
                                        setSort('inbox', 'asc');
                                    }
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                                color={
                                    field === 'inbox'
                                        ? 'primary'
                                        : 'secondary-dark'
                                }
                            >
                                {field === 'inbox'
                                    ? order === 'asc'
                                        ? 'Sort by inbox (asc)'
                                        : 'Sort by inbox (desc)'
                                    : 'Sort by inbox'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (field === 'sender') {
                                        setSort(
                                            'sender',
                                            order === 'asc' ? 'desc' : 'asc'
                                        );
                                    } else {
                                        setSort('sender', 'asc');
                                    }
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                                color={
                                    field === 'sender'
                                        ? 'primary'
                                        : 'secondary-dark'
                                }
                            >
                                {field === 'sender'
                                    ? order === 'asc'
                                        ? 'Sort by sender (asc)'
                                        : 'Sort by sender (desc)'
                                    : 'Sort by sender'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (field === 'created_at') {
                                        setSort(
                                            'created_at',
                                            order === 'asc' ? 'desc' : 'asc'
                                        );
                                    } else {
                                        setSort('created_at', 'asc');
                                    }
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                                color={
                                    field === 'created_at'
                                        ? 'primary'
                                        : 'secondary-dark'
                                }
                            >
                                {field === 'created_at'
                                    ? order === 'asc'
                                        ? 'Sort by created (asc)'
                                        : 'Sort by created (desc)'
                                    : 'Sort by created'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (field === 'updated_at') {
                                        setSort(
                                            'updated_at',
                                            order === 'asc' ? 'desc' : 'asc'
                                        );
                                    } else {
                                        setSort('updated_at', 'asc');
                                    }
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                                color={
                                    field === 'updated_at'
                                        ? 'primary'
                                        : 'secondary-dark'
                                }
                            >
                                {field === 'updated_at'
                                    ? order === 'asc'
                                        ? 'Sort by updated (asc)'
                                        : 'Sort by updated (desc)'
                                    : 'Sort by updated'}
                            </Button>
                        </div>
                    </div>

                    {!isValidating && data && data.length === 0 && (
                        <p>No recipients found in the allowlist.</p>
                    )}
                    {error && (
                        <p className={styles.error}>
                            ⚠ There was an error loading the allowlist.
                            {
                                // We don't know the shape of the error object
                                // so we just print it out.
                                ' ' + error.toString()
                            }
                            <Button
                                onClick={() => {
                                    toast.promise(mutate(), {
                                        loading: 'Refreshing...',
                                        success: 'Refreshed allowlist',
                                        error: 'Error refreshing allowlist',
                                    });
                                }}
                                size='sm'
                                type='button'
                                uitype='ghost'
                            >
                                Retry
                            </Button>
                        </p>
                    )}

                    <div
                        className={classNames(
                            styles.allowlistItems,
                            itemStyles.allowlistItems
                        )}
                    >
                        {Array.isArray(data) && data.length > 0 && (
                            <>
                                <div className={itemStyles.allowlistHeader}>
                                    <div className={itemStyles.column}>
                                        Sender Name
                                    </div>
                                    <div className={itemStyles.column}>
                                        Inbox ID
                                    </div>
                                    <div
                                        className={classNames(
                                            itemStyles.column,
                                            itemStyles.sender
                                        )}
                                    >
                                        Sender Address
                                    </div>
                                    <div className={itemStyles.column}>
                                        Sender Exceptions
                                    </div>
                                    {showLisa && (
                                        <div
                                            className={classNames(
                                                itemStyles.column,
                                                itemStyles.shrinkWidth
                                            )}
                                        >
                                            LISA
                                        </div>
                                    )}
                                    {showAutoQuote && (
                                        <div className={itemStyles.column}>
                                            Auto-Quote
                                        </div>
                                    )}
                                    {showTmsOrders && (
                                        <div className={itemStyles.column}>
                                            TMS Order Entry
                                        </div>
                                    )}
                                    {showReplier && (
                                        <div
                                            className={classNames(
                                                itemStyles.column,
                                                itemStyles.shrinkWidth
                                            )}
                                        >
                                            Replier Demo
                                        </div>
                                    )}
                                    {showWinLoss && (
                                        <div
                                            className={classNames(
                                                itemStyles.column,
                                                itemStyles.shrinkWidth
                                            )}
                                        >
                                            Win-Loss
                                        </div>
                                    )}
                                    <div className={itemStyles.column}>
                                        Updated By
                                    </div>
                                    <div className={itemStyles.column}>
                                        Updated At
                                    </div>
                                    <div className={itemStyles.column}>
                                        Created At
                                    </div>
                                    <div className={itemStyles.action} />
                                </div>
                                {data.map((item) => (
                                    <AllowlistItem
                                        key={item.inbox_sender}
                                        item={item}
                                        showLisa={showLisa}
                                        showAutoQuote={showAutoQuote}
                                        showTmsOrders={showTmsOrders}
                                        showReplier={showReplier}
                                        showWinLoss={showWinLoss}
                                        selectedCompanyid={companyList[company]}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export { InboxSenderAllowlistTable };
